/**
 * Cross-platform download utility
 * - Web: Uses blob URL + <a>.click() pattern
 * - Capacitor native: Saves directly to Documents folder via Filesystem API
 */

const isNativeApp = () => {
  return (
    typeof window !== "undefined" &&
    window.Capacitor?.isNativePlatform?.() === true
  );
};

const sanitizeFilename = (name) => {
  return name.replace(/[^a-zA-Z0-9._\-() ]/g, "_").replace(/\.{2,}/g, ".");
};

const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const stringToBase64 = (str) => {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(str);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/**
 * Download a file cross-platform
 * @param {Blob|ArrayBuffer|string} data - File data
 * @param {string} filename - Filename with extension
 * @param {string} mimeType - MIME type (e.g., 'text/plain', 'application/pdf')
 * @returns {Promise<{saved: boolean, path?: string}>} Result with save path on native
 */
export const downloadFile = async (data, filename, mimeType) => {
  const safeName = sanitizeFilename(filename);

  if (isNativeApp()) {
    const { Filesystem, Directory } = await import("@capacitor/filesystem");

    let base64Data;
    if (data instanceof Blob) {
      base64Data = await blobToBase64(data);
    } else if (data instanceof ArrayBuffer) {
      base64Data = arrayBufferToBase64(data);
    } else if (typeof data === "string") {
      base64Data = stringToBase64(data);
    } else {
      throw new Error("Unsupported data type");
    }

    // Save directly to Documents folder (accessible in Files app)
    const result = await Filesystem.writeFile({
      path: safeName,
      data: base64Data,
      directory: Directory.Documents,
      recursive: true,
    });

    console.log("📁 File saved to Documents:", result.uri);
    return { saved: true, path: result.uri };
  } else {
    // Web browser path
    let blob;
    if (data instanceof Blob) {
      blob = data;
    } else if (data instanceof ArrayBuffer) {
      blob = new Blob([data], { type: mimeType });
    } else if (typeof data === "string") {
      blob = new Blob([data], { type: mimeType });
    } else {
      throw new Error("Unsupported data type");
    }

    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = safeName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      URL.revokeObjectURL(url);
    }
    return { saved: true };
  }
};
